import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Edit2, FileText, CheckCircle2, Upload, Trash2, Loader2, ChevronDown, Plus, Save, AlertTriangle, AlertCircle, Download, LayoutTemplate, RefreshCw, BookOpen } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' }
];
const YEARS = Array.from({ length: 11 }, (_, i) => String(2020 + i));

const NitroReportManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sections');
  const [activeSection, setActiveSection] = useState('header');
  const [sections, setSections] = useState({});
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [saving, setSaving] = useState(false);

  // Previous reports state
  const [storedReports, setStoredReports] = useState([]);
  const [uploadMonth, setUploadMonth] = useState('');
  const [uploadYear, setUploadYear] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(null); // {reportId, filename, status, elapsed}
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [editingSections, setEditingSections] = useState({});
  const [savingSections, setSavingSections] = useState(false);
  const [addSectionId, setAddSectionId] = useState('');

  // Track original prompts for data sections to detect changes and allow revert
  const originalDataPromptsRef = useRef({});
  const [showStructuredWarning, setShowStructuredWarning] = useState(false);

  // Template state
  const [templateInfo, setTemplateInfo] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  const templateFileRef = useRef(null);

  // Define all 16 sections with their default prompts
  // type: 'text' = prose output (editable prompt + examples allowed)
  // type: 'data' = JSON/calculated output (view-only prompt, no examples)
  const defaultSections = {
    header: {
      id: 'header',
      name: 'Header / Page Identifier',
      column: 'left',
      type: 'data',
      grounding: false,
      prompt: 'Generate the header/page identifier for the Nitro valuation report.\n\nUsing the report metadata from the context, return a JSON object with title, subtitle, and report_subject fields.\nUse the quarter and year to determine the correct month abbreviation (e.g., Q2 2025 = Jun.25).\nReturn ONLY the JSON object.',
      examples: []
    },
    key_valuation_metrics: {
      id: 'key_valuation_metrics',
      name: 'Key Valuation Metrics',
      column: 'left',
      type: 'data',
      grounding: false,
      prompt: 'Using the valuation analysis and financial data from the context, calculate Share Price (fair value midpoint), Market Cap, Net Debt/Cash, Enterprise Value, and Change in Share Price (% change from previous quarter).\n\nReturn JSON: {"share_price", "shares_on_issue", "market_cap", "net_debt_cash", "enterprise_value", "change_in_share_price"}\nReturn ONLY the JSON object.',
      examples: []
    },
    jaade_carrying_value: {
      id: 'jaade_carrying_value',
      name: 'JAADE Carrying Value',
      column: 'left',
      type: 'data',
      grounding: false,
      prompt: 'Using the executive summary data from the context (JAADE shares, proposed share price, initial investment cost/date, capital returned), calculate Shares Held, Carrying Value, and Investment IRR.\n\nReturn JSON: {"shares_held", "carrying_value", "investment_irr"}\nReturn ONLY the JSON object.',
      examples: []
    },
    cap_table: {
      id: 'cap_table',
      name: 'Cap Table',
      column: 'left',
      type: 'data',
      grounding: false,
      prompt: 'Using the JAADE ownership percentage from the context, calculate the cap table breakdown: Potentia (~55% of remaining), HVP (~25%), Others (~20%).\n\nReturn JSON: {"potentia", "hvp", "ellerston_jaade", "others", "total"}\nReturn ONLY the JSON object.',
      examples: []
    },
    company_overview: {
      id: 'company_overview',
      name: 'Company Overview',
      column: 'left',
      type: 'text',
      grounding: false,
      prompt: 'Write a Company Overview for Nitro Software (2 paragraphs, 150-200 words).\n\nUsing ALL data from the context, cover: company background, product suite, customer footprint, and recent developments from the qualitative updates.\nStart with "Nitro Software is". Professional investment report tone.\nReturn as plain text only.',
      examples: []
    },
    investment_thesis: {
      id: 'investment_thesis',
      name: 'Investment Thesis',
      column: 'left',
      type: 'text',
      grounding: false,
      prompt: 'Write the Investment Thesis (2-3 paragraphs).\n\nUsing ALL data from the context, cover: SMB market opportunity, integrated product strategy, pricing advantage, scalable SaaS model, and reference strategic initiatives from the qualitative updates.\nProfessional investment language. Return as plain text only.',
      examples: []
    },
    key_risks: {
      id: 'key_risks',
      name: 'Key Risks / Points to Monitor',
      column: 'left',
      type: 'text',
      grounding: false,
      prompt: 'Write the Key Risks section (2 paragraphs).\n\nUsing data from the context, assess: competitive landscape, SMB segment challenges, channel dependency, and product differentiation risks. Be specific and balanced.\nProfessional investment language. Return as plain text only.',
      examples: []
    },
    outlook: {
      id: 'outlook',
      name: 'Outlook',
      column: 'left',
      type: 'text',
      grounding: false,
      prompt: 'Write the Outlook section (2 paragraphs).\n\nUsing ALL data from the context, cover: strategic transformation, growth trajectory, and forward-looking narrative referencing operational milestones and financial metrics.\nProfessional, balanced tone. Return as plain text only.',
      examples: []
    },
    recommendation: {
      id: 'recommendation',
      name: 'Recommendation',
      column: 'right',
      type: 'data',
      grounding: false,
      prompt: 'You must return ONLY a JSON object, nothing else.\n\nUsing ALL data from the context, generate the Recommendation section.\n\nReturn this exact JSON format:\n{\n  "fair_value_per_share": "$3.85",\n  "valuation_change": "+0.0%",\n  "text": "2-3 paragraph recommendation text here..."\n}\n\nRULES:\n1. "fair_value_per_share": the CURRENT JAADE weighted average carrying value per share from the valuation Excel (e.g. "$3.85"). This is the existing/maintained share price — NOT a newly proposed price derived from EV/shares. Use the \'Current Share Price\' or \'Weighted Average Share Price\' field directly from the Excel data.\n2. "valuation_change": the percentage change vs the PREVIOUS quarter\'s carrying value (e.g. "+0.0%" if no change, "+1.7%" if uplift, "-2.3%" if writedown). Calculate from the previous quarter share price in the context.\n3. "text": 2-3 paragraphs stating whether the JAADE team is proposing a change or no change to the current share price, the rationale, key financial figures (ARR, Cash EBITDA, Rule of 40, growth rates), and the overall valuation narrative. Use actual numbers. Professional, confident tone.\n4. Return ONLY the JSON object.\n\nNOTE: The "fair_value_per_share" shown in the report header box is the MAINTAINED/PROPOSED carrying value — it should match what the Key Valuation Metrics section shows as the current share price.',
      examples: []
    },
    discount_to_comps: {
      id: 'discount_to_comps',
      name: 'Discount to Public Comps',
      column: 'right',
      type: 'text',
      grounding: false,
      prompt: 'Write the Discount to Public Comps section (1-2 paragraphs).\n\nUsing valuation and trading multiple data from the context, cover: implied EV, EV/NTM revenue multiple, discount to listed comps, and historical discounting patterns.\nUse actual numbers from the context. Return as plain text only.',
      examples: []
    },
    continue_strong_operating_performance: {
      id: 'continue_strong_operating_performance',
      name: 'Continue Strong Operating Performance',
      column: 'right',
      type: 'text',
      grounding: false,
      prompt: 'Write the "Continue Strong Operating Performance" section for Nitro Software\'s valuation report.\n\nUsing ALL the financial performance data from the context above, write 3-4 paragraphs in a single cohesive narrative covering:\n\n1. ARR & Revenue: Current ARR level and YoY growth rate, total revenue trajectory, revenue composition expectations, and how this quarter\'s ARR performance compares to prior periods.\n2. Retention Metrics: Net Revenue Retention (NRR) performance and what it indicates about expansion revenue, Gross Revenue Retention (GRR) and what it indicates about churn, and overall customer satisfaction and stickiness implications.\n3. Cash & Profitability: Cash position and total debt levels, debt covenant headroom, Cash EBITDA performance and operating EBITDA, and Rule of 40 score and what it indicates about the growth-profitability balance.\n\nUse the actual numbers from the context. Professional investment report tone.\nDo NOT return JSON — return plain text paragraphs separated by newlines.\nDo NOT add any headers, titles, or markdown formatting.',
      examples: []
    },
    precedent_transactions: {
      id: 'precedent_transactions',
      name: 'Precedent Transactions',
      column: 'right',
      type: 'text',
      grounding: false,
      prompt: 'Write the precedent transactions section (1 paragraph).\n\nUsing valuation triangulation data from the context, reference precedent multiples and explain how they support the proposed valuation.\nUse actual data. Return as plain text only.',
      examples: []
    },
    lre_methodology_table: {
      id: 'lre_methodology_table',
      name: 'Fair Value Methodology Table',
      column: 'right',
      type: 'data',
      grounding: false,
      prompt: `You must return ONLY a JSON object, nothing else.

Extract ALL values for the Fair Value Methodology table directly from the "Long run earnings (in 4yrs)" table. This table is located either:
- In the "LRE & Scenarios" sheet/tab of the attached valuation Excel workbook, OR
- In a standalone CSV file called "LRE & Scenarios"

Do NOT calculate, estimate or infer any values. Simply read each row value exactly as it appears in that table.

The table has these 15 rows in order — extract each one:
1.  EV/NTM Revenue multiple          (e.g. "5.0x")
2.  Liquidity Discount               (e.g. "20%")
3.  Deal Multiple                    (e.g. "4.0x")
4.  Net Revenue – 4 Years ($m)       (e.g. "250.0")
5.  Enterprise Value ($m) – Implied  (e.g. "1,000.0")
6.  EBITDA Margin – Steady State     (e.g. "25%")
7.  Estimated EBITDA ($m)            (e.g. "62.5")
8.  Implied EV/EBITDA                (e.g. "16.0x")
9.  Plus Adjusted Cash/(Debt) ($m)   (e.g. "50.0" or "-50.0")
10. Equity Valuation ($m)            (e.g. "1,050.0")
11. Discount Rate                    (e.g. "25%")
12. Equity Valuation ($m) – Today    (e.g. "430.0")
13. Net Debt ($m) – Today            (e.g. "-50.0")
14. Enterprise Value ($m) – Today    (e.g. "380.0")
15. Implied Share Price              (e.g. "$2.15")

Return this exact JSON format:
{
  "intro_text": "Fair Value has been determined using 'Long Run Earnings', which has been commonly applied to high-growth technology companies, focused on a steady-state projection of earnings with a medium-term time horizon, which is discounted back (at 25% in this case) to determine the current value of the company.",
  "lre_table": {
    "ev_ntm_revenue":           "value from row 1",
    "liquidity_discount":       "value from row 2",
    "deal_multiple":            "value from row 3",
    "net_revenue_year4":        "value from row 4",
    "enterprise_value_implied": "value from row 5",
    "ebitda_margin_steady":     "value from row 6",
    "estimated_ebitda":         "value from row 7",
    "implied_ev_ebitda":        "value from row 8",
    "adjusted_cash_debt":       "value from row 9",
    "equity_valuation":         "value from row 10",
    "discount_rate":            "value from row 11",
    "equity_valuation_today":   "value from row 12",
    "net_debt_today":           "value from row 13",
    "enterprise_value_today":   "value from row 14",
    "implied_share_price":      "value from row 15"
  }
}

FORMATTING RULES:
- Copy values exactly as shown in the source table.
- If a percentage is stored as a decimal (e.g. 0.20 meaning 20%), convert it to "20%".
- If a multiple is stored as a plain number (e.g. 4.0 meaning 4.0x), append the "x" suffix: "4.0x".
- Keep all other formatting (commas, dollar signs, decimal places) exactly as-is.
- The "intro_text" field must always contain exactly the sentence shown above — do not modify it.
- Return ONLY the JSON object. No markdown fences, no explanation.`,
      examples: []
    },
    key_financials_table: {
      id: 'key_financials_table',
      name: 'Key Financials & Valuation Multiples',
      column: 'bottom',
      type: 'data',
      grounding: false,
      prompt: 'Using ALL financial data from the context, generate the Key Financials table.\n\nBuild with actual figures for current year, project forward using growth rate, calculate NTM and valuation multiples.\nReturn JSON with cy24_actual, cy25_budget, ntm, and valuation_multiples.\nReturn ONLY the JSON object.',
      examples: []
    },
    // ── PAGE 2 sections ─────────────────────────────────────────
    fair_value_conclusion: {
      id: 'fair_value_conclusion',
      name: 'Fair Value Conclusion',
      column: 'page2',
      type: 'text',
      grounding: false,
      prompt: 'Write the "Fair Value Conclusion" section for a Nitro Software investment valuation report.\n\nUsing ALL the data from the context above — including the recommended/proposed share price, Enterprise Value, EV/NTM Revenue multiple, financial performance data (ARR growth, Cash EBITDA, Rule of 40), peer comparison multiples, and the LRE methodology results — write the Fair Value Conclusion as formal prose suitable for an institutional investor report.\n\nThe conclusion should follow this structure and style:\n1. Opening statement: State whether the JAADE team is proposing a CHANGE or NO CHANGE to Nitro\'s weighted average share price, include the specific share price (e.g. "$3.85"), the implied Enterprise Value (e.g. "$910.0m"), and the implied EV/NTM Revenue multiple (e.g. "4.6x").\n2. Bullet points (each starting with a dash or bullet marker) covering:\n   - Whether the carrying value represents fair value as of the reporting date\n   - Recent trading performance: ARR growth rate, Cash EBITDA performance vs budget, Rule of 40 trajectory\n   - The current EV and what it implies for the multiple, how it compares to public comps (discount %), and how it compares to the entry multiple at initial investment (April 2023); note operational improvements since investment (EBITDA turnaround, profitability)\n   - Why the discount to public comps is appropriate (lack of liquidity, lower market cap vs public comps median, partly offset by control premium)\n   - How Fair Value is supported by the Long Run Earnings Model (project steady state earnings, discount back at the applicable rate)\n   - How the assessment of fair value is supported by triangulation of different valuation techniques (EV/Forward Revenues, EV/LTM revenues, DCF, Sum of the Parts, Long Run Earnings)\n   - Statement that valuation is consistent with IPEV and AIC valuation guidelines\n\nUse ONLY actual figures from the context data — do NOT use placeholder values or generic numbers.\nWrite in formal, factual investment report prose.\nDo NOT return JSON — return plain text with bullet points.\nDo NOT add any titles, headers, or markdown formatting.',
      examples: []
    },
    trading_multiple_evolution: {
      id: 'trading_multiple_evolution',
      name: 'Trading Multiple Evolution (Chart)',
      column: 'page2',
      type: 'data',
      grounding: false,
      prompt: 'You must return ONLY a JSON object, nothing else.\n\nUsing the trading multiple evolution data and historical EV data from the context above, generate the Trading Multiple Evolution chart data and analysis.\n\nReturn this JSON format:\n{\n  "chart_data": [\n    {"date": "MMM-YY", "ev": "XXX.X", "nitro_multiple": "X.X", "comps_multiple": "X.X"},\n    ... generate 8-10 quarters of data leading up to the current quarter\n  ],\n  "description": "Write a 2-3 paragraph analysis covering: (1) The trend in Enterprise Value growth, (2) The discount in Nitro\'s multiples vs public comps over time, (3) Key inflection points and drivers, (4) Implications for valuation convergence potential."\n}\n\nUse the actual multiples data from the context (Australian SaaS comps, global direct comps) for the comps_multiple values.\nReturn ONLY the JSON object.',
      examples: []
    },
    // ── PAGE 3 sections ─────────────────────────────────────────
    background: {
      id: 'background',
      name: 'Background',
      column: 'page3',
      type: 'text',
      grounding: false,
      prompt: 'Write the "Background" section for a Nitro Software investment valuation report.\n\nUsing ALL the data from the context above (including previous reports, AI valuation analysis, and financial data), write 5-6 paragraphs covering:\n\n1. Company background: founding year (2005), positioning as an alternative to Adobe, market position in SaaS document productivity\n2. Core product: Nitro Productivity Suite (PDF editing + eSignature), integrations with Salesforce, competition with Adobe and DocuSign\n3. Customer base: number of business customers, countries, licensed users\n4. JAADE investment: Ellerston JAADE Australian Private Assets Fund investment alongside Potentia Capital, transaction details (total value, acquisition structure, delisting from ASX)\n5. Investment structure: entry price, EV at transaction, loan note structure, conversion to MIT units, Securities Subscription Agreements (ordinary shares 70% / redeemable preference shares 30%)\n6. Capital events: any recapitalization, debt facilities, capital returned to investors\n\nWrite in formal, factual investment report prose. Use specific numbers from the data where available.\nDo NOT return JSON — return plain text paragraphs separated by newlines.\nDo NOT add any headers, titles, or markdown formatting.',
      examples: []
    },
    trading_multiple_commentary: {
      id: 'trading_multiple_commentary',
      name: 'Trading Multiple Commentary',
      column: 'page3',
      type: 'text',
      grounding: false,
      prompt: 'Write the "Trading Multiple Evolution" commentary section for a Nitro Software investment valuation report.\n\nUsing ALL the data from the context above (Enterprise Value, EV/NTM Revenue multiples, public comps multiples, ARR, growth rates, EBITDA, Rule of 40), write 2-3 paragraphs covering:\n\n1. Nitro\'s current implied EV/NTM Revenue multiple vs where public comps are trading, and the discount percentage. Note whether this discount is consistent with historical levels\n2. How the current valuation multiple compares to the entry multiple at the time of initial investment (April 2023), and what operational improvements justify the valuation\n3. The trajectory of profitability improvement (Cash EBITDA trend, Rule of 40 progression) and what this implies for valuation convergence\n\nUse ONLY the actual figures from the context data — do NOT use placeholder numbers.\nWrite in formal investment report prose.\nDo NOT return JSON — return plain text paragraphs separated by newlines.\nDo NOT add any headers, titles, or markdown formatting.',
      examples: []
    },
    // ── PAGE 4 sections ─────────────────────────────────────────
    assessment_of_fair_value: {
      id: 'assessment_of_fair_value',
      name: 'Assessment of Fair Value',
      column: 'page4',
      type: 'text',
      grounding: false,
      prompt: 'Write the "Assessment of Fair Value" section for a Nitro Software investment valuation report.\n\nUsing ALL available data from the context above — especially the previous quarterly valuation reports, the current quarter\'s financial data, the current recommended share price, and the Enterprise Value — write a chronological history of all valuation changes made across every quarterly report.\n\nFORMAT REQUIREMENTS:\n- Each quarterly valuation event must be its own bullet point, starting with exactly the character "►" (no other bullet characters)\n- Work chronologically from the earliest valuation to the current quarter\n- Each bullet must follow this pattern:\n  "► In the valuation report dated [Month Year], we [increased/proposed no change to/decreased] Nitro\'s weighted average share price from $X.XX to $X.XX (+X% uplift / no change / -X% decrease), implying an EV of $XXXm [based on reason / valuing the business at $XXXm]."\n- For the CURRENT quarter\'s report being generated, use the recommended share price from the context as the new value\n- Be specific: include the old price, new price, % change, EV, and 1 sentence of context/reason for each change\n- Use ONLY actual figures from the context data — never use placeholder values\n\nIMPORTANT:\n- Do NOT return JSON\n- Do NOT add a title, header, or any text before the first bullet\n- Each bullet should be on its own line, separated by a blank line\n- Use the ► character (U+25BA) exactly as the bullet marker\n- Match the tone and style of a formal institutional investment report',
      examples: []
    },
    review_of_operational_factors: {
      id: 'review_of_operational_factors',
      name: 'Review of Operational Factors',
      column: 'page4',
      type: 'data',
      grounding: false,
      prompt: 'You must return ONLY a JSON object, nothing else.\n\nUsing ALL available data from the context above — financial data, AI valuation summaries, previous quarterly reports, operational milestones — generate the "Review of Operational Factors" section.\n\nReturn this JSON format:\n{\n  "intro": "In determining Fair Value, we consider the operational performance of the company and/or our underlying investment thesis. We conclude that the company has [performed strongly/maintained performance/faced headwinds] since our initial investment, noting the following:",\n  "bullets": [\n    {\n      "title": "Improved Management Team",\n      "text": "Narrative about management changes, key hires, leadership improvements.",\n      "sub_items": []\n    },\n    {\n      "title": "Improving Profitability",\n      "text": "",\n      "sub_items": [\n        {"label": "Operating Expenses", "text": "Specific opex figures and trends."},\n        {"label": "Cash EBITDA %", "text": "Current Cash EBITDA % vs budget."},\n        {"label": null, "text": "Cash balance narrative."}\n      ]\n    }\n  ],\n  "performance_vs_base_case": "Performance Versus Initial Base Case – 2-3 sentences comparing actual vs base case projections at initial investment."\n}\n\nRULES:\n- Use ONLY actual figures from the context\n- Add or remove bullets as appropriate for the current quarter\'s data\n- sub_items is an empty array [] if no sub-items\n- Return ONLY the JSON object',
      examples: []
    },
    financial_performance: {
      id: 'financial_performance',
      name: 'Financial Performance',
      column: 'page4',
      type: 'data',
      grounding: false,
      prompt: 'You must return ONLY a JSON object, nothing else.\n\nUsing ALL available data from the context above — including the financial table screenshot (if provided as an image), the valuation Excel data, previous quarterly reports, and all financial metrics — generate the "Financial Performance" section.\n\nReturn this JSON format:\n{\n  "intro": "In assessing Fair Value, we consider the financial performance of the company since our initial investment; we make the following observations:",\n  "bullets": [\n    {\n      "text": "Nitro\'s trading performance to [period] has been [strong/mixed/challenging]",\n      "sub_bullets": [\n        "Nitro\'s consolidated revenue at [period] was $[X]m ([X]% ahead/behind budget).",\n        "ARR at [period] was $[X]m (+[X]% YoY).",\n        "SaaS retention metrics: NRR [X]%, GRR [X]%.",\n        "Gross Margin [period] is [X]%."\n      ]\n    },\n    {\n      "text": "On a profitability basis, the company has been performing [better/in-line/below expectations]: [Cash EBITDA] for [period] is $[X]m ([X]% margin).",\n      "sub_bullets": []\n    },\n    {\n      "text": "The company has a healthy cash balance of $[X]m as at [date].",\n      "sub_bullets": []\n    }\n  ]\n}\n\nRULES:\n- Use ONLY actual figures from the context and the financial table image\n- Add or remove bullets/sub_bullets as appropriate\n- sub_bullets is an empty array [] if none\n- Return ONLY the JSON object',
      examples: []
    },
    valuation_summary: {
      id: 'valuation_summary',
      name: 'Valuation Summary',
      column: 'page4',
      type: 'data',
      grounding: false,
      prompt: 'You must return ONLY a JSON object, nothing else.\n\nYou are generating the "Valuation Summary" section for a Nitro Software investment valuation report.\n\nCRITICAL: The valuation chart data MUST be extracted from the "summary" sheet of the attached Excel workbook. Look for a table showing valuation ranges for each methodology (EV/NTM Revenue, EV/LTM Revenue, DCF, SOTP, Long Run Earnings). Extract the low, high, and point-estimate (base case) enterprise values for each.\n\nReturn this exact JSON format:\n{\n  "bullets": [\n    "In addition to monitoring underlying financial performance and operating conditions, the carrying value of Nitro is evaluated via a triangulation of several methods, including EV/Forward Revenues, EV/LTM Revenues, Discounted Cash Flow, Sum of the Parts and Long Run Earnings Analysis.",\n    "As shown below, Nitro\'s Enterprise Value of $[LRE_EV]m was derived from Long Run Earnings approach and is also in line with public comps in terms of historical discount. DCF suggests a lower valuation range due to the conservative inputs used in the exercise (WACC of [WACC]% and terminal growth rate of [TGR]%). The EV/LTM Revenue approach is punitive to Nitro given its higher growth rate than comps.",\n    "A simple average of these methods yields an EV of $[avg_EV]m, which is not materially ([pct_diff]%) different from our Long Run Earnings approach, upon which Nitro\'s Enterprise Value of $[LRE_EV]m is based on."\n  ],\n  "chart_data": [\n    {"method": "EV/NTM Revenue", "low": 700, "high": 1200, "point": 920},\n    {"method": "EV/LTM Revenue", "low": 560, "high": 1050, "point": 770},\n    {"method": "DCF",            "low": 630, "high": 1050, "point": 800},\n    {"method": "SOTP",           "low": 730, "high": 1200, "point": 900},\n    {"method": "Long Run Earnings", "low": 720, "high": 1160, "point": 940}\n  ],\n  "average_ev": 888,\n  "average_ev_label": "$888m"\n}\n\nSTRICT RULES:\n1. "chart_data": values MUST come from the "summary" sheet of the attached Excel file. Do NOT make up numbers.\n2. "average_ev": calculate the simple average of ALL "point" values across the 5 chart_data entries (sum of all point estimates divided by 5). This is shown as the vertical dashed reference line on the chart.\n3. "average_ev_label": format as "$[value]m" (e.g. "$888m"), rounded to the nearest whole number.\n4. "bullets": replace all placeholder tokens like [LRE_EV], [avg_EV], [WACC], [TGR], [pct_diff] with real numbers from the data.\n5. Return ONLY the JSON object. No markdown, no commentary.',
      examples: []
    },
    earnings_multiples: {
      id: 'earnings_multiples',
      name: 'Earnings Multiples',
      column: 'page4',
      type: 'data',
      grounding: false,
      prompt: 'You must return ONLY a JSON object, nothing else.\n\nYou are generating the "Earnings Multiples" section for a Nitro Software investment valuation report.\n\nThis section has TWO parts:\n1. A text analysis paragraph discussing the company\'s share price valuation, EV/NTM revenue multiple, and how it compares to public comps (trading multiples). Include details about the peer comparison methodology and the discount/premium to public comps.\n2. A structured "Valuation - Peer Comparison" table with specific financial metrics.\n\nCRITICAL: Extract ALL numerical values from the context data.\n\nReturn this exact JSON format:\n{\n  "text": "Nitro\'s share price of $[share_price] values the company at an EV of $[EV]m and implies an EV/NTM revenue of [multiple]x, which is a [discount]% discount to public comps:\\no\\tTrading Multiples: Australian SaaS and Global direct comps businesses trading at [comps_multiple]x. The peer comparison was done with ASX-listed businesses including fast growth technology platforms with similar growth rates as Nitro (industry average of [ind_avg]% vs Nitro\'s projected [nitro_growth]% ARR growth) and Global direct comps such as Adobe and DocuSign.",\n  "peer_comparison_table": {\n    "price_per_share": "3.85",\n    "shares_millions": "200.5",\n    "market_cap": "771.4",\n    "net_debt": "-138.6",\n    "enterprise_value_aud": "910.0",\n    "enterprise_value_usd": "591.5",\n    "ntm_revenue": "129.1",\n    "underlying_multiple": "4.6x",\n    "listed_multiple_public_comps": "5.3x",\n    "discount_to_public_comps": "-14%"\n  }\n}\n\nSTRICT RULES:\n1. For "text": Write 1-2 paragraphs analyzing the earnings multiples and peer comparison. Replace ALL placeholders with real numbers from the data.\n2. For "peer_comparison_table": ALL values MUST be extracted from the actual data. Do NOT invent numbers.\n3. Return ONLY the JSON object. No markdown, no commentary.',
      examples: []
    }
  };

  useEffect(() => {
    loadConfiguration();
    loadStoredReports();
    loadTemplateInfo();
  }, []);

  const loadConfiguration = async () => {
    try {
      const response = await axios.get(`${API}/reports/nitro/configuration`);
      const savedSections = response.data.sections || {};
      
      // Merge saved sections with defaults (saved takes precedence)
      const mergedSections = {
        ...defaultSections,  // Start with defaults
        ...Object.keys(savedSections).reduce((acc, key) => {
          // For each saved section, merge with default to preserve structure
          acc[key] = {
            ...defaultSections[key],  // Default structure (id, name, column)
            ...savedSections[key]     // Saved data (prompt, examples)
          };
          return acc;
        }, {})
      };
      
      setSections(mergedSections);
      // Capture original data section prompts for change detection
      const dataPrompts = {};
      Object.entries(mergedSections).forEach(([key, section]) => {
        if (section.type === 'data') dataPrompts[key] = section.prompt;
      });
      originalDataPromptsRef.current = dataPrompts;
    } catch (error) {
      // If no saved config, use defaults
      setSections(defaultSections);
      const dataPrompts = {};
      Object.entries(defaultSections).forEach(([key, section]) => {
        if (section.type === 'data') dataPrompts[key] = section.prompt;
      });
      originalDataPromptsRef.current = dataPrompts;
    }
  };

  const getModifiedDataSections = () => {
    return Object.entries(sections).filter(([key, section]) => {
      if (section.type !== 'data') return false;
      return section.prompt !== originalDataPromptsRef.current[key];
    }).map(([key, section]) => section.name);
  };

  const handleSaveConfiguration = async () => {
    console.log('[DEBUG] handleSaveConfiguration called');
    const modifiedDataSections = getModifiedDataSections();
    if (modifiedDataSections.length > 0) {
      setShowStructuredWarning(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/reports/nitro/configuration`, {
        sections
      });
      // Update original prompts to reflect saved state
      const dataPrompts = {};
      Object.entries(sections).forEach(([key, section]) => {
        if (section.type === 'data') dataPrompts[key] = section.prompt;
      });
      originalDataPromptsRef.current = dataPrompts;
      toast.success('Report configuration saved successfully');
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertDataSections = () => {
    setSections(prev => {
      const reverted = { ...prev };
      Object.entries(originalDataPromptsRef.current).forEach(([key, originalPrompt]) => {
        if (reverted[key]) reverted[key] = { ...reverted[key], prompt: originalPrompt };
      });
      return reverted;
    });
    setShowStructuredWarning(false);
    toast.info('Data section prompts reverted to original');
  };

  const loadStoredReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/nitro/previous-reports`);
      setStoredReports(response.data);
    } catch (error) {
      console.error('Error loading stored reports:', error);
    }
  };

  // ── Template functions ──
  const loadTemplateInfo = async () => {
    setTemplateLoading(true);
    try {
      const res = await axios.get(`${API}/nitro/template/info`);
      setTemplateInfo(res.data);
    } catch (error) {
      console.error('Error loading template info:', error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleDownloadStarter = async () => {
    try {
      toast.info('Generating starter template...');
      const res = await axios.get(`${API}/nitro/template/starter`, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Nitro_v3_Starter_Template.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Starter template downloaded! Open in Word, customise, then upload it back.');
    } catch (error) {
      toast.error('Failed to download starter template');
    }
  };

  const handleUploadTemplate = async () => {
    if (!templateFile) { toast.error('Please select a .docx file'); return; }
    if (!templateFile.name.endsWith('.docx')) { toast.error('Only .docx files are accepted'); return; }
    setTemplateUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', templateFile);
      const res = await axios.post(`${API}/nitro/template/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      setTemplateFile(null);
      if (templateFileRef.current) templateFileRef.current.value = '';
      await loadTemplateInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await axios.delete(`${API}/nitro/template`);
      toast.success('Custom template deleted. Auto-generated layout will be used.');
      await loadTemplateInfo();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleUploadReport = async () => {
    if (!uploadFile || !uploadMonth || !uploadYear) {
      toast.error('Please select a file, month, and year');
      return;
    }
    setUploading(true);
    setExtractionProgress({ reportId: null, filename: uploadFile.name, status: 'uploading', elapsed: 0 });
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('month', uploadMonth);
      formData.append('year', uploadYear);
      const res = await axios.post(`${API}/reports/nitro/previous-reports/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Report uploaded. Extracting sections via AI...');
      setExtractionProgress(prev => ({ ...prev, reportId: res.data.id, status: 'extracting', elapsed: 0 }));
      setUploadFile(null);
      setUploadMonth('');
      setUploadYear('');
      loadStoredReports();
      // Start polling for extraction status
      pollExtractionStatus(res.data.id);
    } catch (error) {
      console.error('Error uploading report:', error);
      toast.error('Failed to upload report');
      setExtractionProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const pollExtractionStatus = (reportId) => {
    const startTime = Date.now();
    const interval = setInterval(async () => {
      try {
        // Update elapsed time
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setExtractionProgress(prev => prev ? { ...prev, elapsed } : null);

        const res = await axios.get(`${API}/reports/nitro/previous-reports/${reportId}/status`);
        const status = res.data.extraction_status;
        if (status === 'complete') {
          clearInterval(interval);
          const count = Object.keys(res.data.extracted_sections || {}).length;
          setExtractionProgress(prev => prev ? { ...prev, status: 'complete', sectionCount: count } : null);
          toast.success(`Extraction complete: ${count} section(s) extracted and saved as examples.`);
          loadStoredReports();
          loadConfiguration();
          // Clear the progress card after 4 seconds
          setTimeout(() => setExtractionProgress(null), 4000);
        } else if (status === 'error') {
          clearInterval(interval);
          setExtractionProgress(prev => prev ? { ...prev, status: 'error' } : null);
          toast.error('Section extraction failed. The report was still saved.');
          loadStoredReports();
          setTimeout(() => setExtractionProgress(null), 5000);
        }
      } catch {
        clearInterval(interval);
        setExtractionProgress(null);
      }
    }, 3000);
    // Safety: stop polling after 5 minutes
    setTimeout(() => { clearInterval(interval); setExtractionProgress(null); }, 300000);
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/reports/nitro/previous-reports/${reportId}`);
      toast.success('Report deleted');
      if (expandedReportId === reportId) setExpandedReportId(null);
      loadStoredReports();
      loadConfiguration();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  // Text sections that can be extracted
  const TEXT_SECTIONS = [
    { id: 'company_overview', name: 'Company Overview' },
    { id: 'investment_thesis', name: 'Investment Thesis' },
    { id: 'key_risks', name: 'Key Risks / Points to Monitor' },
    { id: 'outlook', name: 'Outlook' },
    { id: 'recommendation', name: 'Recommendation' },
    { id: 'discount_to_comps', name: 'Discount to Public Comps' },
    { id: 'continue_strong_operating_performance', name: 'Continue Strong Operating Performance' },
    { id: 'precedent_transactions', name: 'Precedent Transactions' },
    { id: 'fair_value_conclusion', name: 'Fair Value Conclusion' },
    { id: 'background', name: 'Background' },
    { id: 'trading_multiple_commentary', name: 'Trading Multiple Commentary' },
    { id: 'earnings_multiples', name: 'Earnings Multiples' },
  ];

  const handleExpandReport = (reportId) => {
    if (expandedReportId === reportId) {
      setExpandedReportId(null);
      setEditingSections({});
    } else {
      const report = storedReports.find(r => r.id === reportId);
      setExpandedReportId(reportId);
      setEditingSections({ ...(report?.extracted_sections || {}) });
      setAddSectionId('');
    }
  };

  const handleSectionTextChange = (sectionId, text) => {
    setEditingSections(prev => ({ ...prev, [sectionId]: text }));
  };

  const handleDeleteSection = (sectionId) => {
    setEditingSections(prev => {
      const copy = { ...prev };
      delete copy[sectionId];
      return copy;
    });
  };

  const handleAddSection = () => {
    if (!addSectionId || editingSections[addSectionId] !== undefined) {
      toast.error('Select a section that has not been extracted yet');
      return;
    }
    setEditingSections(prev => ({ ...prev, [addSectionId]: '' }));
    setAddSectionId('');
  };

  const handleSaveSections = async (reportId) => {
    setSavingSections(true);
    try {
      await axios.put(`${API}/reports/nitro/previous-reports/${reportId}/sections`, {
        extracted_sections: editingSections,
      });
      toast.success('Sections saved and examples updated');
      loadStoredReports();
      loadConfiguration();
    } catch (error) {
      toast.error('Failed to save sections');
    } finally {
      setSavingSections(false);
    }
  };

  const handlePromptEdit = (sectionId, newPrompt) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        prompt: newPrompt
      }
    }));
  };

  const handleGroundingToggle = (sectionId) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        grounding: !prev[sectionId]?.grounding
      }
    }));
  };

  const [newExampleText, setNewExampleText] = useState('');

  // Prompt variants state
  const [variantNameInput, setVariantNameInput] = useState('');
  const [showVariantInput, setShowVariantInput] = useState(false);

  const handleSaveVariant = (sectionId) => {
    console.log('[DEBUG] handleSaveVariant called for section:', sectionId);
    const name = variantNameInput.trim();
    console.log('[DEBUG] Variant name:', name);
    if (!name) { toast.error('Please enter a name for this variant'); return; }
    const currentPrompt = sections[sectionId]?.prompt || '';
    const newVariant = { id: `v_${Date.now()}`, name, prompt: currentPrompt };
    console.log('[DEBUG] Creating new variant:', newVariant);
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        prompt_variants: [...(prev[sectionId]?.prompt_variants || []), newVariant]
      }
    }));
    setVariantNameInput('');
    setShowVariantInput(false);
    console.log('[DEBUG] Showing success toast');
    toast.success(`Prompt saved as "${name}"`);
  };

  const handleUseVariant = (sectionId, variant) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], prompt: variant.prompt }
    }));
    toast.success(`Loaded "${variant.name}"`);
  };

  const handleDeleteVariant = (sectionId, variantId) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        prompt_variants: (prev[sectionId]?.prompt_variants || []).filter(v => v.id !== variantId)
      }
    }));
  };

  const handleAddTextExample = (sectionId) => {
    if (!newExampleText.trim()) {
      toast.error('Please enter example text before adding');
      return;
    }
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        examples: [...(prev[sectionId]?.examples || []), newExampleText.trim()]
      }
    }));
    setNewExampleText('');
    toast.success(`Example added for ${sections[sectionId]?.name}`);
  };

  const updateExample = (sectionId, exampleIndex, newText) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        examples: prev[sectionId].examples.map((ex, i) => i === exampleIndex ? newText : ex)
      }
    }));
  };

  const removeExample = (sectionId, exampleIndex) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        examples: prev[sectionId].examples.filter((_, i) => i !== exampleIndex)
      }
    }));
  };

  const currentSection = sections[activeSection] || defaultSections[activeSection];
  const isTextSection = currentSection?.type === 'text';
  const leftSections = Object.values(sections).filter(s => s.column === 'left');
  const rightSections = Object.values(sections).filter(s => s.column === 'right');
  const bottomSections = Object.values(sections).filter(s => s.column === 'bottom');
  const page2Sections = Object.values(sections).filter(s => s.column === 'page2');
  const page3Sections = Object.values(sections).filter(s => s.column === 'page3');
  const page4Sections = Object.values(sections).filter(s => s.column === 'page4');

  return (
    <>
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Manage Nitro v3 Report</h1>
                <p className="text-sm text-slate-600">Configure sections, prompts, and examples</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveConfiguration} disabled={saving} style={{ backgroundColor: '#A8A67A' }}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Top-level Tabs */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="sections" data-testid="sections-tab">
              <Edit2 className="w-4 h-4 mr-2" />
              Section Prompts
            </TabsTrigger>
            <TabsTrigger value="previous-reports" data-testid="previous-reports-tab">
              <FileText className="w-4 h-4 mr-2" />
              Previous Reports ({storedReports.length})
            </TabsTrigger>
            <TabsTrigger value="template" data-testid="template-tab">
              <LayoutTemplate className="w-4 h-4 mr-2" />
              Word Template
            </TabsTrigger>
          </TabsList>

          {/* Sections Tab */}
          <TabsContent value="sections">
            <div className="grid grid-cols-12 gap-6">
          {/* Section List Sidebar */}
          <div className="col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Report Sections</CardTitle>
                <CardDescription className="text-xs">22 sections across 5 pages</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {/* Left Column Sections */}
                <div className="px-4 py-2 bg-slate-100 text-xs font-semibold">Page 1 — Left Column</div>
                {leftSections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50 transition-colors ${
                      activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{section.name}</span>
                      {section.examples?.length > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                  </button>
                ))}

                {/* Right Column Sections */}
                <div className="px-4 py-2 bg-slate-100 text-xs font-semibold mt-2">Page 1 — Right Column</div>
                {rightSections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50 transition-colors ${
                      activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{section.name}</span>
                      {section.examples?.length > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                  </button>
                ))}

                {/* Bottom Section */}
                <div className="px-4 py-2 bg-slate-100 text-xs font-semibold mt-2">Page 1 — Bottom</div>
                {bottomSections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50 transition-colors ${
                      activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{section.name}</span>
                      {section.examples?.length > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                  </button>
                ))}

                {/* Page 2 Sections */}
                <div className="px-4 py-2 bg-slate-100 text-xs font-semibold mt-2">Page 2</div>
                {page2Sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50 transition-colors ${
                      activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{section.name}</span>
                      {section.examples?.length > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                  </button>
                ))}

                {/* Page 3 Sections */}
                <div className="px-4 py-2 bg-slate-100 text-xs font-semibold mt-2">Page 3</div>
                {page3Sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50 transition-colors ${
                      activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{section.name}</span>
                      {section.examples?.length > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                  </button>
                ))}

                {/* Page 4 Sections */}
                <div className="px-4 py-2 bg-slate-100 text-xs font-semibold mt-2">Page 4</div>
                {page4Sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-4 py-3 text-sm border-b hover:bg-slate-50 transition-colors ${
                      activeSection === section.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{section.name}</span>
                      {section.examples?.length > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Section Detail */}
          <div className="col-span-9">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{currentSection?.name}</CardTitle>
                    <CardDescription>
                      {isTextSection
                        ? 'Configure the prompt and add example outputs for this section'
                        : 'This section outputs structured data (JSON). Edits require confirmation on save.'}
                    </CardDescription>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${isTextSection ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {isTextSection ? 'Text Output' : 'Data Output'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="prompt" className="w-full">
                  <TabsList>
                    <TabsTrigger value="prompt">
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Prompt
                    </TabsTrigger>
                    {isTextSection && (
                      <TabsTrigger value="examples" data-testid="examples-tab">
                        <FileText className="w-4 h-4 mr-2" />
                        Examples ({currentSection?.examples?.length || 0})
                      </TabsTrigger>
                    )}
                  </TabsList>

                  {/* Prompt Tab */}
                  <TabsContent value="prompt" className="space-y-4">
                    <div>
                      <Label className="text-base font-semibold mb-2 block">
                        AI Prompt {!isTextSection && <span className="text-sm font-normal text-amber-600">(Structured Output)</span>}
                      </Label>
                      <p className="text-sm text-slate-600 mb-3">
                        {isTextSection
                          ? 'This prompt will be sent to AI when generating this section. Variables in curly braces will be replaced with actual data.'
                          : 'This prompt generates structured data (JSON). Changes will require confirmation before saving.'}
                      </p>
                      <Textarea
                        value={currentSection?.prompt || ''}
                        onChange={(e) => handlePromptEdit(activeSection, e.target.value)}
                        rows={20}
                        className="font-mono text-xs"
                        data-testid="section-prompt-textarea"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                      <div>
                        <Label className="text-sm font-semibold">Web-search grounding</Label>
                        <p className="text-xs text-slate-500 mt-0.5">Allow AI to search the web for current information when generating this section</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={currentSection?.grounding || false}
                        data-testid="grounding-toggle"
                        onClick={() => handleGroundingToggle(activeSection)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${currentSection?.grounding ? 'bg-green-500' : 'bg-slate-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentSection?.grounding ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>

                    {/* Saved Prompt Variants */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100 border-b">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-slate-500" />
                          <span className="text-sm font-semibold text-slate-700">Saved Prompts</span>
                          {(currentSection?.prompt_variants?.length || 0) > 0 && (
                            <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
                              {currentSection.prompt_variants.length}
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => { setShowVariantInput(v => !v); setVariantNameInput(''); }}
                        >
                          <Plus className="w-3 h-3" />
                          Save current as variant
                        </Button>
                      </div>

                      {/* Save as variant input */}
                      {showVariantInput && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b">
                          <input
                            className="flex-1 text-sm border rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-amber-400"
                            placeholder="Variant name (e.g. Conservative, Q1 version…)"
                            value={variantNameInput}
                            onChange={e => setVariantNameInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSaveVariant(activeSection, e); } if (e.key === 'Escape') setShowVariantInput(false); }}
                            autoFocus
                          />
                          <Button type="button" size="sm" className="h-7 text-xs" style={{ backgroundColor: '#A8A67A', color: 'white' }} onClick={(e) => handleSaveVariant(activeSection, e)}>
                            <Save className="w-3 h-3 mr-1" /> Save
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowVariantInput(false); }}>Cancel</Button>
                        </div>
                      )}

                      {/* Variants list */}
                      {(currentSection?.prompt_variants?.length || 0) === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-slate-400">
                          No saved variants yet. Edit the prompt above and click "Save current as variant" to store it.
                        </div>
                      ) : (
                        <div className="divide-y">
                          {currentSection.prompt_variants.map((variant) => (
                            <div key={variant.id} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 group">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{variant.name}</p>
                                <p className="text-xs text-slate-400 mt-0.5 truncate">{variant.prompt.substring(0, 80)}…</p>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-50"
                                  onClick={() => handleUseVariant(activeSection, variant)}
                                >
                                  Use
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleDeleteVariant(activeSection, variant.id)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* Examples Tab - only for text sections */}
                  {isTextSection && (
                    <TabsContent value="examples" className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold mb-2 block">
                          Example Outputs
                        </Label>
                        <p className="text-sm text-slate-600 mb-3">
                          Paste examples of this section from previous reports. These will be sent to AI as reference for the expected style, tone, and format.
                        </p>

                        {/* Add new text example */}
                        <div className="border rounded-lg p-4 mb-4 space-y-3">
                          <Label className="text-sm font-medium">Add a new example</Label>
                          <Textarea
                            value={newExampleText}
                            onChange={(e) => setNewExampleText(e.target.value)}
                            placeholder="Paste an example of the expected output for this section..."
                            rows={8}
                            className="w-full"
                            data-testid="new-example-textarea"
                          />
                          <Button
                            onClick={() => handleAddTextExample(activeSection)}
                            disabled={!newExampleText.trim()}
                            size="sm"
                            data-testid="add-example-btn"
                          >
                            Add Example
                          </Button>
                        </div>

                        {/* List of existing examples */}
                        {currentSection?.examples && currentSection.examples.length > 0 && (
                          <div className="space-y-3">
                            <Label className="text-sm font-semibold">Saved Examples ({currentSection.examples.length}):</Label>
                            {currentSection.examples.map((example, index) => (
                              <div key={index} className="border rounded-lg p-4 bg-slate-50">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-slate-700">Example {index + 1}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeExample(activeSection, index)}
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`remove-example-${index}`}
                                  >
                                    Remove
                                  </Button>
                                </div>
                                <Textarea
                                  value={typeof example === 'string' ? example : example?.text || JSON.stringify(example)}
                                  onChange={(e) => updateExample(activeSection, index, e.target.value)}
                                  rows={5}
                                  className="text-sm"
                                  data-testid={`edit-example-${index}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {(!currentSection?.examples || currentSection.examples.length === 0) && (
                          <p className="text-sm text-slate-400 italic">No examples added yet. Adding examples helps AI match the expected output style.</p>
                        )}
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>

          {/* Previous Reports Tab */}
          <TabsContent value="previous-reports">
            <div className="grid grid-cols-12 gap-6">
              {/* Upload Form */}
              <div className="col-span-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Upload Report</CardTitle>
                    <CardDescription className="text-xs">
                      Upload a previous quarter report PDF. The system will use AI to extract section examples word-for-word and save them to the Section Prompts tab.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Report File (PDF)</Label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setUploadFile(e.target.files[0] || null)}
                        className="text-sm w-full"
                        data-testid="upload-report-file"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm font-medium mb-1 block">Month</Label>
                        <Select value={uploadMonth} onValueChange={setUploadMonth}>
                          <SelectTrigger data-testid="upload-month-select">
                            <SelectValue placeholder="Month" />
                          </SelectTrigger>
                          <SelectContent>
                            {MONTHS.map(m => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium mb-1 block">Year</Label>
                        <Select value={uploadYear} onValueChange={setUploadYear}>
                          <SelectTrigger data-testid="upload-year-select">
                            <SelectValue placeholder="Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {YEARS.map(y => (
                              <SelectItem key={y} value={y}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button
                      onClick={handleUploadReport}
                      disabled={uploading || !uploadFile || !uploadMonth || !uploadYear}
                      className="w-full"
                      style={{ backgroundColor: '#A8A67A' }}
                      data-testid="upload-report-btn"
                    >
                      {uploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" />Upload Report</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Stored Reports List */}
              <div className="col-span-8">
                {/* Extraction Progress Card */}
                {extractionProgress && (
                  <Card className="mb-4 border-2 border-blue-200 bg-blue-50/50">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3 mb-3">
                        {extractionProgress.status === 'uploading' && (
                          <>
                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-blue-800">Uploading {extractionProgress.filename}...</p>
                              <p className="text-xs text-blue-600">Sending file to server</p>
                            </div>
                          </>
                        )}
                        {extractionProgress.status === 'extracting' && (
                          <>
                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-800">Extracting sections from {extractionProgress.filename}</p>
                              <p className="text-xs text-blue-600">
                                AI is reading and extracting 17 sections from the PDF... ({extractionProgress.elapsed}s)
                              </p>
                            </div>
                          </>
                        )}
                        {extractionProgress.status === 'complete' && (
                          <>
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-green-800">
                                Extraction complete — {extractionProgress.sectionCount} sections extracted!
                              </p>
                              <p className="text-xs text-green-600">Sections saved as examples for AI generation</p>
                            </div>
                          </>
                        )}
                        {extractionProgress.status === 'error' && (
                          <>
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-red-800">Extraction failed</p>
                              <p className="text-xs text-red-600">The report was saved but section extraction encountered an error</p>
                            </div>
                          </>
                        )}
                      </div>
                      {/* Progress bar */}
                      {(extractionProgress.status === 'uploading' || extractionProgress.status === 'extracting') && (
                        <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full transition-all duration-1000 ease-out"
                            style={{
                              backgroundColor: '#A8A67A',
                              width: extractionProgress.status === 'uploading'
                                ? '15%'
                                : `${Math.min(15 + (extractionProgress.elapsed / 90) * 80, 95)}%`,
                            }}
                          />
                        </div>
                      )}
                      {extractionProgress.status === 'complete' && (
                        <div className="w-full bg-green-100 rounded-full h-2 overflow-hidden">
                          <div className="h-2 rounded-full bg-green-500" style={{ width: '100%' }} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Stored Reports</CardTitle>
                    <CardDescription className="text-xs">
                      Click a report to view and edit its extracted sections. Changes sync to Section Prompts examples.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {storedReports.length === 0 ? (
                      <p className="text-sm text-slate-400 italic py-4">No reports uploaded yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {storedReports.map(report => {
                          const isExpanded = expandedReportId === report.id;
                          const sectionCount = Object.keys(report.extracted_sections || {}).length;
                          return (
                            <div key={report.id} className="border rounded-lg overflow-hidden" data-testid={`stored-report-${report.id}`}>
                              {/* Report header row */}
                              <div
                                className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-slate-100 border-b' : 'bg-white'}`}
                                onClick={() => handleExpandReport(report.id)}
                                data-testid={`expand-report-${report.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
                                  <div>
                                    <p className="text-sm font-medium">{report.filename}</p>
                                    <p className="text-xs text-slate-500">
                                      {MONTHS.find(m => m.value === report.month)?.label || report.month} {report.year}
                                    </p>
                                  </div>
                                  {report.extraction_status === 'processing' && (
                                    <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                      <Loader2 className="w-3 h-3 animate-spin" /> Extracting...
                                    </span>
                                  )}
                                  {report.extraction_status === 'complete' && (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                      <CheckCircle2 className="w-3 h-3" /> {sectionCount} section{sectionCount !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                  {report.extraction_status === 'error' && (
                                    <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">Extraction failed</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteReport(report.id); }}
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`delete-report-${report.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>

                              {/* Expanded section editor */}
                              {isExpanded && (
                                <div className="p-4 bg-white space-y-4" data-testid={`report-sections-${report.id}`}>
                                  {Object.keys(editingSections).length === 0 && (
                                    <p className="text-sm text-slate-400 italic">No sections extracted. Add a section below.</p>
                                  )}

                                  {Object.entries(editingSections).map(([sectionId, text]) => {
                                    const sectionName = TEXT_SECTIONS.find(s => s.id === sectionId)?.name || sectionId;
                                    return (
                                      <div key={sectionId} className="border rounded-lg p-3 bg-slate-50" data-testid={`section-block-${sectionId}`}>
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-sm font-semibold text-slate-700">{sectionName}</span>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteSection(sectionId)}
                                            className="text-red-500 hover:text-red-700 h-7 px-2"
                                            data-testid={`delete-section-${sectionId}`}
                                          >
                                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                                          </Button>
                                        </div>
                                        <Textarea
                                          value={text}
                                          onChange={(e) => handleSectionTextChange(sectionId, e.target.value)}
                                          rows={5}
                                          className="text-sm"
                                          data-testid={`section-textarea-${sectionId}`}
                                        />
                                      </div>
                                    );
                                  })}

                                  {/* Add section */}
                                  <div className="flex items-end gap-2 pt-2 border-t">
                                    <div className="flex-1">
                                      <Label className="text-xs font-medium mb-1 block">Add a section</Label>
                                      <Select value={addSectionId} onValueChange={setAddSectionId}>
                                        <SelectTrigger className="h-9" data-testid="add-section-select">
                                          <SelectValue placeholder="Select section..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {TEXT_SECTIONS.filter(s => editingSections[s.id] === undefined).map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleAddSection}
                                      disabled={!addSectionId}
                                      className="h-9"
                                      data-testid="add-section-btn"
                                    >
                                      <Plus className="w-4 h-4 mr-1" /> Add
                                    </Button>
                                  </div>

                                  {/* Save button */}
                                  <div className="flex justify-end pt-2">
                                    <Button
                                      onClick={() => handleSaveSections(report.id)}
                                      disabled={savingSections}
                                      size="sm"
                                      style={{ backgroundColor: '#A8A67A' }}
                                      data-testid="save-sections-btn"
                                    >
                                      {savingSections ? (
                                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</>
                                      ) : (
                                        <><Save className="w-4 h-4 mr-1" /> Save Sections</>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Word Template Tab */}
          <TabsContent value="template">
            <div className="max-w-3xl mx-auto space-y-6">

              {/* Status Card */}
              <div className="bg-white rounded-xl border shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <LayoutTemplate className="w-5 h-5" style={{ color: '#A8A67A' }} />
                    <h3 className="text-lg font-semibold text-slate-800">Word Template Status</h3>
                  </div>
                  <button onClick={loadTemplateInfo} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600" title="Refresh">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
                {templateLoading ? (
                  <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading...</span></div>
                ) : templateInfo ? (
                  <div className="space-y-3">
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${templateInfo.has_custom_template ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                      {templateInfo.has_custom_template
                        ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        : <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                      <div>
                        <p className="font-medium text-sm text-slate-800">
                          {templateInfo.has_custom_template ? 'Custom Template Active' : 'Using Auto-Generated Layout'}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {templateInfo.has_custom_template
                            ? `File: ${templateInfo.metadata?.filename || 'nitro_custom.docx'} · Uploaded: ${templateInfo.metadata?.uploaded_at ? new Date(templateInfo.metadata.uploaded_at).toLocaleDateString() : 'Unknown'} · ${templateInfo.metadata?.size_bytes ? (templateInfo.metadata.size_bytes / 1024).toFixed(0) + ' KB' : ''}`
                            : 'No custom template uploaded. Upload your own to control fonts, colours and layout.'}
                        </p>
                      </div>
                    </div>
                    {templateInfo.has_custom_template && (
                      <button onClick={handleDeleteTemplate} className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" /> Reset to Auto-Generated Layout
                      </button>
                    )}
                  </div>
                ) : null}
              </div>

              {/* How it works */}
              <div className="bg-slate-50 rounded-xl border p-5 space-y-3">
                <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: '#A8A67A' }} /> How Template-Based Export Works
                </h4>
                <div className="space-y-2 text-sm text-slate-600">
                  {[
                    ['1.', 'The current template (Nitro Template 2.docx) is pre-configured and active — all placeholders are already mapped to live report data.'],
                    ['2.', 'When a report is generated, click "Download Word (Template)" in the report viewer to export a fully populated .docx.'],
                    ['3.', 'Images (Key Financials, Trading Multiples chart, Valuation Summary chart, Earnings Multiples screenshot) are embedded automatically if uploaded at report generation time.'],
                    ['4.', 'To use a different layout: create a new .docx using the same {{ variable }} placeholders from the reference below, then upload it using the uploader.'],
                  ].map(([num, text]) => (
                    <div key={num} className="flex items-start gap-2">
                      <span className="font-bold text-slate-400 mt-0.5 flex-shrink-0">{num}</span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload custom template */}
              <div className="bg-white rounded-xl border shadow-sm p-6">
                <h4 className="font-semibold text-slate-800 mb-1">Upload Custom Template</h4>
                <p className="text-sm text-slate-500 mb-4">Replace the active template with your own .docx. All placeholder variables will be filled with live report data at download time.</p>
                <div
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-colors"
                  onClick={() => templateFileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setTemplateFile(f); }}
                >
                  <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  {templateFile ? (
                    <div><p className="text-sm font-medium text-slate-700">{templateFile.name}</p><p className="text-xs text-slate-400 mt-1">{(templateFile.size / 1024).toFixed(1)} KB</p></div>
                  ) : (
                    <div><p className="text-sm text-slate-500">Click to select or drag and drop</p><p className="text-xs text-slate-400 mt-1">.docx files only, max 20MB</p></div>
                  )}
                  <input ref={templateFileRef} type="file" accept=".docx" className="hidden" onChange={(e) => setTemplateFile(e.target.files[0] || null)} />
                </div>
                {templateFile && (
                  <button onClick={handleUploadTemplate} disabled={templateUploading} className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#A8A67A' }}>
                    {templateUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {templateUploading ? 'Uploading...' : 'Upload Template'}
                  </button>
                )}
              </div>

              {/* Variable Reference */}
              <div className="bg-white rounded-xl border shadow-sm p-6">
                <h4 className="font-semibold text-slate-800 mb-1">Quick Variable Reference</h4>
                <p className="text-xs text-slate-500 mb-4">All placeholders available in the current template. Use these exact names when building a custom template.</p>

                {[
                  {
                    group: 'Cover Page',
                    vars: [
                      ['{{ title }}', 'Report title'],
                      ['{{ report_subject }}', 'Report subject / period (e.g. "Nitro – Sep 2025")'],
                      ['{{ recommendation }}', 'Recommendation text'],
                    ]
                  },
                  {
                    group: 'Key Valuation Metrics (front-page table)',
                    vars: [
                      ['{{ kvm1 }}', 'Share Price'],
                      ['{{ kvm2 }}', 'Shares on Issue (#m)'],
                      ['{{ kvm3 }}', 'Market Cap ($m)'],
                      ['{{ kvm4 }}', 'Net Debt / (Cash) ($m)'],
                      ['{{ kvm5 }}', 'Enterprise Value'],
                      ['{{ kvm6 }}', 'Change in Share Price'],
                    ]
                  },
                  {
                    group: 'JAADE Carrying Value',
                    vars: [
                      ['{{ jcv1 }}', 'Number of Shares Held (#)'],
                      ['{{ jcv2 }}', 'Carrying Value ($m)'],
                      ['{{ jcv3 }}', 'Investment IRR'],
                    ]
                  },
                  {
                    group: 'Cap Table',
                    vars: [
                      ['{{ ct1 }}', 'Potentia %'],
                      ['{{ ct2 }}', 'HVP %'],
                      ['{{ ct3 }}', 'Ellerston JAADE %'],
                      ['{{ ct4 }}', 'Others %'],
                      ['{{ ct5 }}', 'Total %'],
                    ]
                  },
                  {
                    group: 'Fair Value Narrative',
                    vars: [
                      ['{{ continue_strong_operating_performance }}', 'Operating performance narrative (ARR, retention, cash)'],
                      ['{{ discount_to_public_comps }}', 'Discount to public comparables narrative'],
                      ['{{ valuation_supported_by_precendent }}', 'Precedent transactions narrative'],
                      ['{{ fair_value_conclusion }}', 'Fair Value conclusion paragraph'],
                    ]
                  },
                  {
                    group: 'Long Run Earnings (LRE) Table',
                    vars: [
                      ['{{ lre1 }}', 'EV / NTM Revenue'],
                      ['{{ lre2 }}', 'Liquidity Discount'],
                      ['{{ lre3 }}', 'Deal Multiple (x)'],
                      ['{{ lre4 }}', 'Net Revenue – 4 Years ($m)'],
                      ['{{ lre5 }}', 'Enterprise Value ($m) – Implied'],
                      ['{{ lre6 }}', 'EBITDA Margin – Steady State'],
                      ['{{ lre7 }}', 'Estimated EBITDA ($m)'],
                      ['{{ lre8 }}', 'Implied EV / EBITDA'],
                      ['{{ lre9 }}', 'Plus Adjusted Cash / (Debt)'],
                      ['{{ lre10 }}', 'Equity Valuation ($m)'],
                      ['{{ lre11 }}', 'Discount Rate'],
                      ['{{ lre12 }}', 'Equity Valuation ($m) – Today'],
                      ['{{ lre13 }}', 'Net Debt ($m) – Today'],
                      ['{{ lre14 }}', 'Enterprise Value ($m) – Today'],
                      ['{{ lre15 }}', 'Implied Share Price ($)'],
                      ['{{ lre16 }}', '% (Discount) / Premium to Current Share Price'],
                    ]
                  },
                  {
                    group: 'Key Financials Image',
                    vars: [
                      ['{{ key_financials_and_valuation_multiples_image }}', 'Key Financials & Valuation Multiples screenshot (uploaded at generation)'],
                    ]
                  },
                  {
                    group: 'Report Body — Text Sections',
                    vars: [
                      ['{{ company_overview }}', 'Company Overview narrative'],
                      ['{{ investment_thesis }}', 'Investment Thesis narrative'],
                      ['{{ key_risks }}', 'Key Risks / Points to Monitor'],
                      ['{{ outlook }}', 'Outlook narrative'],
                      ['{{ background_text }}', 'Background section'],
                      ['{{ assessment_of_fair_value }}', 'Assessment of Fair Value bullets'],
                      ['{{ review_of_operational_factors }}', 'Review of Operational Factors (incl. sub-items & performance vs base case)'],
                      ['{{ financial_performance_text }}', 'Financial Performance (incl. sub-bullets)'],
                      ['{{ trading_multiple_evolution_text }}', 'Trading Multiple Evolution narrative'],
                      ['{{ valuation_summary_text }}', 'Valuation Summary narrative'],
                    ]
                  },
                  {
                    group: 'Report Body — Chart Images',
                    vars: [
                      ['{{ financial_performance_image }}', 'Financial Performance screenshot (uploaded at generation)'],
                      ['{{ trading_multiple_evolution_image }}', 'Trading Multiple Evolution chart (auto-generated)'],
                      ['{{ valuation_summary_image }}', 'Valuation Summary football-field chart (auto-generated)'],
                    ]
                  },
                  {
                    group: 'Earnings Multiples',
                    vars: [
                      ['{{ earnings_multiple_text }}', 'Earnings Multiples narrative'],
                      ['{{ em1 }}', 'Price per Share (A$) — Nitro Fair Value'],
                      ['{{ em2 }}', 'No. Shares (# millions)'],
                      ['{{ em3 }}', 'Market Cap (A$m)'],
                      ['{{ em4 }}', 'Net Debt (A$m)'],
                      ['{{ em5 }}', 'Enterprise Value (A$m)'],
                      ['{{ em6 }}', 'Enterprise Value (U$m)'],
                      ['{{ em7 }}', 'NTM Revenue ($m)'],
                      ['{{ em8 }}', 'Underlying Multiple (x)'],
                      ['{{ em9 }}', 'Listed Multiple – Public Comps'],
                      ['{{ em10 }}', 'Discount to Public Comps'],
                      ['{{ comparison_to_peers_image }}', 'Peer Comparison screenshot (uploaded at generation)'],
                    ]
                  },
                ].map(({ group, vars }) => (
                  <div key={group} className="mb-5">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{group}</p>
                    <div className="space-y-1">
                      {vars.map(([variable, desc]) => (
                        <div key={variable} className="flex items-start gap-2 py-0.5">
                          <code className="text-xs font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap">{variable}</code>
                          <span className="text-slate-500 text-xs pt-0.5">{desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </TabsContent>


        </Tabs>
      </div>
    </div>

      {/* Structured Output Warning Dialog */}
      <AlertDialog open={showStructuredWarning} onOpenChange={setShowStructuredWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Structured Output Warning
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-2">
              <p>You have modified the prompt for one or more <strong>structured output</strong> sections:</p>
              <ul className="list-disc pl-5 text-sm">
                {getModifiedDataSections().map(name => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
              <p className="text-amber-700 font-medium">
                These sections return structured data (JSON). Changing the prompt may break the expected output format and cause report generation errors.
              </p>
              <p>Are you sure you want to save these changes?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleRevertDataSections}>
              Cancel &amp; Revert
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { setShowStructuredWarning(false); await doSave(); }}
              style={{ backgroundColor: '#A8A67A' }}
            >
              Save Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default NitroReportManager;
